import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { format, isToday, isValid, parseISO } from "date-fns";
import { useCookies } from "react-cookie";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// hooks
import { useActions } from "../../../common/hooks/useActions";
import { useDidMountEffect } from "../../../common/hooks/useDidMountEffect";
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
// icons
import { dateIcon } from "../../../common/vector";
// helpers
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { checkIsItInFutureWithLimit } from "../../../common/utils/helpers/checkIsItInFutureWithLimit";
import { addLeadingZero } from "../../../common/utils/helpers/appLeadingZero";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
// components
import RowAction from "../Uber/UberRowAction";
import AirportTaxiSummary from "./AirportTaxiSummary";
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import AppButton from "../../../components/ui/AppButton";
import CustomGoogleAutocomplete from "../../../components/CustomGoogleAutocomplete";
import PaymentModal from "../../../components/Payment/PaymentModal";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// actions
import { googleSearchCreators } from "../../../common/store/modules/googleSearchModule";
import { airportTaxiActionCreators } from "./airportTaxiModule";
import { errorFieldActionCreators } from "../../../common/store/modules/errorFieldModule";
import { parseAirlineDepartureTime } from "../../../common/utils/helpers/parseAirlineDepartureTime";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";

interface GooglePlaceResult {
  value?: {
    structured_formatting?: {
      secondary_text?: string;
      main_text?: string;
    };
    place_id?: string;
  };
}

// Initialize time from "now" when the component mounts so it's never stale
// (e.g. user stays on homepage 5+ min then navigates here - previously we used module-level
// currentHours/currentMinutes from app load, causing "must be in the future" validation error)
const getInitialHours = () => new Date().getHours();
const getInitialMinutes = () => new Date().getMinutes();

export default function AirportTaxi() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();

  const language = localStorage.getItem("i18nextLng") || "en";

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  // Airline vs hotel context (with sessionStorage fallback, same mechanism as SBB).
  // Hotel flow stays backward compatible (no `location.state` required).
  const airlineNavState = restoredNavState;
  type AirlineRouterStateShape = {
    meta?: { source?: string };
    shared?: any;
  };
  const isAirlineSource = (airlineNavState as AirlineRouterStateShape | undefined)?.meta?.source === "airline";
  const airlineShared = isAirlineSource ? (airlineNavState as AirlineRouterStateShape | undefined)?.shared : null;
  const transportContext = useMemo(() => resolveTransportAirlineApiContext(airlineNavState), [airlineNavState]);
  const airlineDepartureDate = airlineShared?.departureDate || null;
  const airlineTime = airlineShared?.time || null;
  const airlineFinalDestination = airlineShared?.finalDestination || null;
  const airlineFinalDestinationMainText = airlineFinalDestination?.formattedAddress?.main_text || null;
  const airlineFinalDestinationSecondaryText = airlineFinalDestination?.formattedAddress?.secondary_text || null;
  const airlineFinalDestinationPlaceId = airlineFinalDestination?.placeId || null;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [airportTaxiHours, setAirportTaxiHours] = useState(getInitialHours);
  const [airportTaxiMinutes, setAirportTaxiMinutes] = useState(getInitialMinutes);

  // Routes
  const [selectedFromStation, setSelectedFromStation] = useState<GooglePlaceResult | null>(null);
  const [selectedToStation, setSelectedToStation] = useState<GooglePlaceResult | null>(null);
  // set is See Price button is valid to be clicked, if time is valid (current or in the future)
  const [isRequestTimeValid, setIsRequestTimeValid] = useState(true);

  // states
  const airportTaxiInitial = useSelector((state: any) => state.airportTaxi.airportTaxiInitial);
  const airportTaxiInitialItem = useSelector((state: any) => state.airportTaxi.airportTaxiInitialItem);
  const selectedRide = useSelector((state: any) => state.airportTaxi.selectedRide);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const isLoadingGeocodingDeparture = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDeparture);
  const isLoadingGeocodingDestination = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDestination);
  const geocodingDeparture = useSelector((state: any) => state.googleSearch.geocodingDeparture);
  const geocodingDestination = useSelector((state: any) => state.googleSearch.geocodingDestination);
  const isValidDepartureAddress = useSelector((state: any) => state.googleSearch.isValidDepartureAddress);
  const isValidDestinationAddress = useSelector((state: any) => state.googleSearch.isValidDestinationAddress);
  const estimationAvailable = useSelector((state: any) => state.airportTaxi.estimationAvailable);
  const airportTaxiEstimationError = useSelector((state: any) => state.airportTaxi.airportTaxiEstimationError);
  const showAirportTaxiPaymentModal = useSelector((state: any) => state.airportTaxi.showPaymentModal);
  const airportTaxiBookingResponseData = useSelector((state: any) => state.airportTaxi.bookingResponseData);
  const googlePlacesApiKey = useSelector((state: any) => state.global.googlePlacesApiKey);

  // actions
  const getGoogleGeocoding = useActions(googleSearchCreators?.getGoogleGeocoding, []);
  const setIsValidGoogleFromToAddress = useActions(googleSearchCreators?.setIsValidGoogleFromToAddress, []);
  const resetGoogleGeocodingFromToState = useActions(googleSearchCreators?.resetGoogleGeocodingFromToState, []);
  const getAirportTaxiInitial = useActions(airportTaxiActionCreators?.getAirportTaxiInitialAction, []);
  const resetAirportTaxiState = useActions(airportTaxiActionCreators?.resetAirportTaxiStateAction, []);
  const getAirportTaxiEstimation = useActions(airportTaxiActionCreators?.getEstimationAction, []);
  const buyAirportTaxiTicket = useActions(airportTaxiActionCreators?.buyTicketAction, []);
  const toggleAirportTaxiPaymentModal = useActions(airportTaxiActionCreators?.togglePaymentModalAction, []);
  const toggleAirportTaxiBookingError = useActions(airportTaxiActionCreators?.toggleBookingErrorAction, []);
  const setAirportTaxiErrorMessage = useActions(airportTaxiActionCreators?.setErrorMessageAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  const timeLimitAmount: number = airportTaxiInitialItem?.time_limit ? airportTaxiInitialItem?.time_limit : 0;
  const lastAirportTaxiInitialItemKeyRef = useRef<any>(null);
  const lastInitialFetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (tenantInfo) {
      const fetchKey =
        transportContext.mode === "airline"
          ? `airline:${transportContext.iataCode}`
          : transportContext.mode === "airline_missing_iata"
          ? "airline:missing_iata"
          : "hotel";
      if (lastInitialFetchKeyRef.current === fetchKey) {
        return;
      }
      lastInitialFetchKeyRef.current = fetchKey;

      getAirportTaxiInitial(transportContext);
    }
  }, [tenantInfo, getAirportTaxiInitial, transportContext]);

  // Persist airline router state to survive React Router state loss across internal navigations/reloads.
  useEffect(() => {
    persistAirlineFromNavigationState(airlineNavState);
  }, [airlineNavState]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/airport-taxi",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportContext.mode === "airline" ? transportContext.iataCode : null,
    onProviderLanguageChange: () => getAirportTaxiInitial(transportContext)
  });

  useEffect(() => {
    if (!airportTaxiInitialItem) return;
    // Hotel flow needs "initial" time adjustment (based on time limits).
    // For airline we want to use incoming time (when date is today) and default 06:00 for future dates.
    if (isAirlineSource) return;
    const airportTaxiInitialItemKey = airportTaxiInitialItem?.code ?? airportTaxiInitialItem?.id ?? null;
    if (lastAirportTaxiInitialItemKeyRef.current === airportTaxiInitialItemKey) return;
    lastAirportTaxiInitialItemKeyRef.current = airportTaxiInitialItemKey;

    updateMinutes(
      setAirportTaxiMinutes,
      setAirportTaxiHours,
      "initial",
      timeLimitAmount + 5,
      airportTaxiHours,
      airportTaxiMinutes
    );
  }, [airportTaxiInitialItem, isAirlineSource, airportTaxiHours, airportTaxiMinutes, timeLimitAmount]);

  // --- AIRLINE EXTENSION ---
  // Prefill departure date: behave like a user-selected value.
  useEffect(() => {
    if (!isAirlineSource || !airlineDepartureDate) return;

    const parsed = parseISO(airlineDepartureDate);
    if (!isValid(parsed)) return;

    setSelectedDate(parsed);
    toggleAirportTaxiBookingError(false);
  }, [isAirlineSource, airlineDepartureDate, toggleAirportTaxiBookingError]);
  // --- END OF AIRLINE EXTENSION ---

  useEffect(() => {
    // reset on unmount
    return () => {
      resetGoogleGeocodingFromToState();
      resetAirportTaxiState();
      resetErrorField();
      clearPersistedAirlineRouterState();
    };
  }, [resetGoogleGeocodingFromToState, resetAirportTaxiState, resetErrorField]);

  useEffect(() => {
    if (selectedRide) {
      setSelectedFromStation({
        value: {
          structured_formatting: {
            main_text: selectedRide?.departure_address?.name,
            secondary_text: selectedRide?.departure_address?.formatted
          },
          place_id: ""
        }
      });
      setSelectedToStation({
        value: {
          structured_formatting: {
            main_text: selectedRide?.destination_address?.name,
            secondary_text: selectedRide?.destination_address?.formatted
          },
          place_id: ""
        }
      });

      setIsValidGoogleFromToAddress("departure", true);
      setIsValidGoogleFromToAddress("destination", true);
    }
  }, [selectedRide, setIsValidGoogleFromToAddress]);

  // --- AIRLINE EXTENSION ---
  // Override destination with airline payload so pricing uses the correct final destination.
  // This runs again after `selectedRide` changes, because the existing flow overwrites destination from API.
  useEffect(() => {
    if (!isAirlineSource) return;
    if (!airlineFinalDestinationPlaceId || !airlineFinalDestinationSecondaryText) return;

    setSelectedToStation({
      value: {
        structured_formatting: {
          main_text: airlineFinalDestinationMainText || "",
          secondary_text: airlineFinalDestinationSecondaryText
        },
        place_id: airlineFinalDestinationPlaceId
      }
    });
    setIsValidGoogleFromToAddress("destination", true);
  }, [
    isAirlineSource,
    airlineFinalDestinationPlaceId,
    airlineFinalDestinationMainText,
    airlineFinalDestinationSecondaryText,
    selectedRide,
    setIsValidGoogleFromToAddress
  ]);
  // --- END OF AIRLINE EXTENSION ---

  // Check if the selected date is in the past (with limit). This effect should fire on the every date/time changes
  useEffect(() => {
    setIsRequestTimeValid(
      checkIsItInFutureWithLimit(selectedDate, airportTaxiHours, airportTaxiMinutes, 0, timeLimitAmount)
    );
  }, [selectedDate, airportTaxiHours, airportTaxiMinutes, timeLimitAmount]);

  // US 5353: For departure dates in the future: Make departure time 6.00 am (hardcoded) by default
  useDidMountEffect(() => {
    if (!isToday(new Date(selectedDate))) {
      setAirportTaxiHours(6);
      setAirportTaxiMinutes(0);
    } else {
      // if the selected date is today:
      // - airline: use incoming time (mimic user input)
      // - hotel: use current time + time limit offset (existing behavior)
      if (isAirlineSource) {
        const parsedTime = parseAirlineDepartureTime(airlineTime);
        if (parsedTime) {
          setAirportTaxiHours(parsedTime.hours);
          setAirportTaxiMinutes(parsedTime.minutes);
          return;
        }
      }

      const now = new Date();
      updateMinutes(
        setAirportTaxiMinutes,
        setAirportTaxiHours,
        "initial",
        timeLimitAmount + 5,
        now.getHours(),
        now.getMinutes()
      );
    }
  }, [selectedDate, isAirlineSource, airlineTime]);

  // Handle ROUTE selecting departure (from) address
  useDidMountEffect(() => {
    const isValidAddress = geocodingDeparture?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("departure", isValidAddress);
    updateDepartureAddress(geocodingDeparture);
  }, [geocodingDeparture]);

  // Handle ROUTE selecting destination (to) address
  useDidMountEffect(() => {
    const isValidAddress = geocodingDestination?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("destination", isValidAddress);
    updateDestinationAddress(geocodingDestination);
  }, [geocodingDestination]);

  const shouldEnableSeePriceBtn = (): boolean => {
    const areRoutesValid: boolean = isValidDepartureAddress && isValidDestinationAddress;
    return areRoutesValid && isRequestTimeValid;
  };

  const renderPrebookErrorMsg = () => {
    let errors: Array<any> = [];

    if (selectedFromStation && !isValidDepartureAddress) {
      errors.push({
        errorMessage: t("transport.departureAddressInvalid")
      });
    }
    if (selectedToStation && !isValidDestinationAddress) {
      errors.push({
        errorMessage: t("transport.destinationAddressInvalid")
      });
    }

    if (!isValidDestinationAddress || !isValidDepartureAddress) {
      errors.push({
        errorMessage: t("transport.requiredAddresses"),
        info: true
      });
    }

    if (airportTaxiEstimationError) {
      errors.push({
        errorMessage: t("transport.noAvailableVehicle")
      });
    }

    // Time limit error message:
    // if time_limit exists AND bigger than 0 > show custom time_limit error msg, otherwise show default/global time error msg
    if (
      airportTaxiInitialItem &&
      !checkIsItInFutureWithLimit(selectedDate, airportTaxiHours, airportTaxiMinutes, 0, timeLimitAmount)
    ) {
      const isTimeLimitExists: boolean = airportTaxiInitialItem?.time_limit && airportTaxiInitialItem?.time_limit > 0;
      errors.push({
        errorMessage: isTimeLimitExists
          ? t("transport.timeLimitErrorMsg", { limit: airportTaxiInitialItem?.time_limit })
          : t("transport.timeErrorMsg")
      });
    }

    return errors?.map((error: any, i: number) =>
      error?.errorMessage ? (
        <div key={i} className={`airport-taxi__error-msg ${error?.info ? "airport-taxi__error-msg--info" : ""}`}>
          {error?.field && `${error?.field} -`} {error?.errorMessage}
        </div>
      ) : null
    );
  };

  const handleSeePriceRideBtn = () => {
    setAirportTaxiErrorMessage([]);

    const isTimeValidOnClick: boolean = checkIsItInFutureWithLimit(
      selectedDate,
      airportTaxiHours,
      airportTaxiMinutes,
      0,
      timeLimitAmount
    );
    if (isTimeValidOnClick) {
      // handle getAirportTaxiEstimation if time is valid
      let time;
      let date;
      time = addLeadingZero(airportTaxiHours) + ":" + addLeadingZero(airportTaxiMinutes);
      date = format(new Date(selectedDate), "yyyy-MM-dd");

      getAirportTaxiEstimation(
        {
          time,
          date,
          departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text,
          departureAddressId: selectedFromStation?.value?.place_id ? selectedFromStation?.value?.place_id : null,
          destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text,
          destinationAddressId: selectedToStation?.value?.place_id,
          offer_code: isAirlineSource ? airlineShared?.offer_code : undefined,
          milesAndMore: isAirlineSource ? airlineShared?.milesAndMore ?? "" : ""
        },
        transportContext
      );
    } else {
      // If isTimeValidOnClick is not true, we should update state and set it to isRequestTimeValid to false
      setIsRequestTimeValid(false);
    }
  };

  // Handling from-to methods:

  const updateDepartureAddress = (address: any) => {
    setSelectedFromStation(address);
  };

  const updateDestinationAddress = (address: any) => {
    setSelectedToStation(address);
  };

  const handleDepartureAddress = (address: any, action: any) => {
    if (action.action === "clear") return;
    handleGeocoding(address, "departure");
  };

  const handleDestinationAddress = (address: any, action: any) => {
    if (action.action === "clear") return;
    handleGeocoding(address, "destination");
  };

  const onClearRouteInput = (input: any) => {
    const isDepartureInput = input === "first_input";
    if (isDepartureInput) {
      setIsValidGoogleFromToAddress("departure", false);
      updateDepartureAddress(null);

      toggleAirportTaxiBookingError(false);
    } else {
      // destination is mandatory, so when user clicks on X we should set destination validation on false/not-valid
      setIsValidGoogleFromToAddress("destination", false);
      updateDestinationAddress(null);

      toggleAirportTaxiBookingError(false);
    }
  };

  const handleGeocoding = (address: any, route: any) => {
    getGoogleGeocoding(address?.value?.place_id, route);
  };

  // END OF Handling from-to methods:

  const getDisabledStyleForTicketsForm = () => {
    if (estimationAvailable) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  const handleBookARideBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: selectedRide?.code ?? selectedRide?.offer_code ?? airportTaxiInitialItem?.code ?? null
    });

    buyAirportTaxiTicket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000), // get unix timestamp
        timeslot: `${airportTaxiHours}:${airportTaxiMinutes}`,
        departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text,
        departureAddressId: selectedFromStation?.value?.place_id ? selectedFromStation?.value?.place_id : null,
        destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text,
        destinationAddressId: selectedToStation?.value?.place_id
      },
      navigate,
      transportContext
    );
  };

  return (
    <Grid
      className="airport-taxi grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      <>
        <TransportProviderHeader
          title={airportTaxiInitial?.provider_title ?? airportTaxiInitial?.title}
          titleClassName="airport-taxi__title"
          shortDescription={airportTaxiInitial?.provider_description}
          providerLogo={airportTaxiInitial?.provider_logo}
        />

        <Grid container>
          <Grid
            item
            sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
            md={6}
            xs={12}
          >
            <RowAction
              label={t("transport.departureDate")}
              icon={dateIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={selectedDate}
                    setSelectedDate={date => {
                      setSelectedDate(date ?? new Date());
                      toggleAirportTaxiBookingError(false);
                    }}
                    minDate={new Date()}
                  />
                </Grid>
              }
            />
            <RowAction
              label={""}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  {airportTaxiInitial?.highlight ? (
                    <Box
                      key={airportTaxiInitial.highlight}
                      sx={{
                        textAlign: "left",
                        fontSize: 13,
                        "& p": { my: 0 },
                        "& p + p": { mt: 2 }
                      }}
                      dangerouslySetInnerHTML={{ __html: airportTaxiInitial.highlight }}
                    />
                  ) : (
                    <Box sx={{ whiteSpace: "pre-line", textAlign: "left", lineHeight: 1 }}>
                      {t("transport.fmsTaxiInfo")}
                    </Box>
                  )}
                </Grid>
              }
            />
          </Grid>
          <Grid item md={6} xs={12} mt={{ sm: 6, xs: 6, md: 0 }} position={"relative"}>
            <div className="vertical-divider" />
            <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
              <Box sx={{ ...getDisabledStyleForTicketsForm() }}>
                {googlePlacesApiKey && (
                  <CustomGoogleAutocomplete
                    apiKey={googlePlacesApiKey}
                    inputTestIdOfFirstInput="airport-taxi-departure"
                    inputTestIdOfSecondInput="airport-taxi-destination"
                    valueOfFirstInput={selectedFromStation}
                    valueOfSecondInput={selectedToStation}
                    setValueOfFirstInput={handleDepartureAddress}
                    setValueOfSecondInput={handleDestinationAddress}
                    onClear={onClearRouteInput}
                    isLoadingFirstInput={isLoadingGeocodingDeparture}
                    isLoadingSecondInput={isLoadingGeocodingDestination}
                    showStar={true}
                    location={{
                      lat: 0,
                      lng: 0
                    }}
                    isEditableFirstInput={false}
                  />
                )}
                <Box mt={1}>{renderPrebookErrorMsg()}</Box>
                <Grid
                  container={isAirlineSource}
                  item={!isAirlineSource}
                  xs={12}
                  sm={isAirlineSource ? undefined : 4}
                  sx={{
                    mt: 3,
                    ...(isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                  }}
                >
                  {isAirlineSource && (
                    <Grid item sx={{ flex: 1 }}>
                      <AppButton
                        label={t("transport.goBack")}
                        onClick={() => navigateBackOrToMain(navigate, { language })}
                        disableRipple={true}
                        customBackgroundColor={theme.palette.primary.dark}
                        customHoverColor={theme.palette.primary.dark}
                        fontSize="14px"
                        dataAttribute="taxi-go-back-1"
                      />
                    </Grid>
                  )}

                  <Grid
                    item
                    sx={{
                      opacity: shouldEnableSeePriceBtn() ? 1 : 0.8,
                      ...(isAirlineSource ? { flex: 1 } : {})
                    }}
                  >
                    <AppButton
                      label={t("transport.seePriceBtn")}
                      onClick={handleSeePriceRideBtn}
                      disableRipple={true}
                      customBackgroundColor={tenantInfo?.style?.button?.color}
                      customActiveColor={tenantInfo?.style?.button?.active}
                      customHoverColor={tenantInfo?.style?.button?.hover}
                      customClickColor={tenantInfo?.style?.button?.click}
                      fontSize="14px"
                      disabled={!shouldEnableSeePriceBtn()}
                      dataAttribute="taxi-see-price"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
            {estimationAvailable && (
              <AirportTaxiSummary
                onBookARideBtn={handleBookARideBtn}
                selectedFromStation={selectedFromStation}
                selectedToStation={selectedToStation}
                cancellationPolicy={selectedRide?.cancellation_policy || airportTaxiInitial?.cancellation_policy}
              />
            )}
          </Grid>
        </Grid>
      </>

      {showAirportTaxiPaymentModal && (
        <PaymentModal
          isOpen={showAirportTaxiPaymentModal}
          bookingResponseData={airportTaxiBookingResponseData}
          togglePaymentModal={toggleAirportTaxiPaymentModal}
          redirectUrl="transport/airport-taxi/confirmation"
        />
      )}
    </Grid>
  );
}
